import dayjs from "dayjs";
import type { Edition, IEdition } from "types";

const VIRTUAL_LEAD_MS = 1 * 60 * 60 * 1000;
const PHYSICAL_LEAD_MS = 2 * 60 * 60 * 1000;

function getRelevantSession(edition: IEdition, now: number) {
  const sessions = [...(edition.sessions ?? [])].sort((a, b) => Number(a.startDate) - Number(b.startDate));
  if (sessions.length === 0) return null;
  const current = sessions.find((s) => now >= Number(s.startDate) && now <= Number(s.endDate));
  const next = sessions.find((s) => now <= Number(s.startDate));
  return current ?? next ?? sessions[sessions.length - 1];
}

export function canJoinEdition(edition: IEdition, isVirtual: boolean, now: number = Date.now()): boolean {
  if (edition.status !== "E") return false;
  const session = getRelevantSession(edition, now);
  const sStart = session ? Number(session.startDate) : NaN;
  const sEnd = session ? Number(session.endDate) : NaN;
  const hasWindow = Number.isFinite(sStart) && Number.isFinite(sEnd);
  const meetingStart = hasWindow ? sStart : Number(edition.startTime);
  const meetingEnd = hasWindow ? sEnd : Number(edition.endTime);
  const lead = isVirtual ? VIRTUAL_LEAD_MS : PHYSICAL_LEAD_MS;
  return now >= meetingStart - lead && now <= meetingEnd;
}

export type EditionJoinAvailability = "available" | "not_yet" | "ended";

export function getEditionJoinAvailability(
  edition: IEdition,
  isVirtual: boolean,
  now: number = Date.now()
): EditionJoinAvailability {
  if (canJoinEdition(edition, isVirtual, now)) return "available";
  const session = getRelevantSession(edition, now);
  const sEnd = session ? Number(session.endDate) : NaN;
  const meetingEnd = Number.isFinite(sEnd) ? sEnd : Number(edition.endTime);
  if (Number.isFinite(meetingEnd) && now > meetingEnd) return "ended";
  return "not_yet";
}

export function getEditionMeetingUrl(edition: IEdition, now: number = Date.now()): string | null {
  const session = getRelevantSession(edition, now);
  return session?.link || edition.link || null;
}

export function oneEditionIsEnrolled(editions: Edition[] | undefined){
  let isAtLeastOneEditionEnrolled = editions?.some(edition => edition.status === "E" || edition.status === "P");

  return isAtLeastOneEditionEnrolled ?? false;
}

export function isEnrolledLoStatus(status?: string){
  return status === "E" || status === "P" || status === "C";
}

export function oneUserEdition(editions: Edition[] | undefined){
  return editions?.some(edition => isEnrolledLoStatus(edition.status)) ?? false;
}

export function isDisabled(
  disabled:boolean,
  isEnrolled:boolean,
  isCompleted:boolean,
  hasAvailablePlaces:boolean,
  editionEndTime:dayjs.Dayjs
){
  if(isEnrolled) return false;
  if(isCompleted) return true;
  if(disabled) return true;
  if(!hasAvailablePlaces) return true;
  if(editionEndTime < dayjs()) return true;

  return false;
}


export function displayRadio(
  selfEnrollment:boolean,
  oneEditionIsEnrolled:boolean,
  editMode:boolean,
  isWaitingApproval:boolean
){
  if(!selfEnrollment) return "none";
  if(isWaitingApproval && !editMode) return "none";
  if(!oneEditionIsEnrolled && selfEnrollment) return "flex";
  if(editMode && selfEnrollment) return "flex";
  if(!editMode && selfEnrollment) return "none";
  if(editMode && !selfEnrollment) return "none";
}


/**
 * Iscrizione calata dall'alto: è l'amministrazione a iscrivere l'utente.
 * Copre "TOP_DOWN", "TOP_MANDATORY" e ogni altra variante che il BE derivi da quelle
 * (il match è sulla sottostringa proprio per non perdere valori non ancora censiti).
 */
export function isTopDownEnrollType(enrollmentEnrollType?: string | null): boolean {
  return !!enrollmentEnrollType && enrollmentEnrollType.includes("TOP");
}

/**
 * Se l'utente può iscriversi da sé a un'edizione dell'aula.
 *
 * La modalità di iscrizione ha la precedenza sul flag della singola aula:
 * - top-down: iscrive l'amministrazione, l'utente può farlo solo se l'aula ha il flag attivo;
 * - auto-iscrizione ("BOTTOM", "PRE_BOTTOM", "AUTO_ENROLL", "REQUESTED_AUTO_ENROLL", ...):
 *   l'iscrizione all'edizione è sempre a carico dell'utente, con o senza flag — quando è
 *   soggetta ad approvazione cambia l'esito della richiesta, non chi la invia;
 * - modalità non nota: si ricade sul solo flag dell'aula.
 */
export function canSelfEnroll(selfEnrollment: boolean, enrollmentEnrollType?: string | null): boolean {
  if (!enrollmentEnrollType) return selfEnrollment;
  if (isTopDownEnrollType(enrollmentEnrollType)) return selfEnrollment;
  return true;
}
